<template>
  <app-custom-dialog
    :value="value"
    title="printer_test"
    title-icon="fal fa-print"
    title-icon-color="primary"
    :footer="false"
    width="960px"
    @input="$emit('input', $event)"
  >
    <div slot="activator">
      <v-container fluid class="printer-test-dialog pa-2 pa-sm-4">
        <!-- Connected printers -->
        <v-row class="mb-6">
          <v-col cols="12">
            <div class="d-flex align-center flex-wrap mb-2">
              <div class="text-subtitle-2 font-weight-medium">
                {{ $t("connected_printers") }}
              </div>
              <v-spacer />
              <v-btn
                text
                small
                color="primary"
                class="printer-test-dialog__refresh"
                :loading="refreshing"
                @click="refreshPrinters"
              >
                <v-icon left small>fal fa-sync</v-icon>
                {{ $t("refresh") }}
              </v-btn>
            </div>
            <v-divider class="mb-4" />

            <v-row
              v-if="!printers || printers.length === 0"
              class="text-center py-6"
            >
              <v-col cols="12">
                <v-icon size="40" color="grey lighten-1">fal fa-print-slash</v-icon>
                <p class="mt-3 mb-4 grey--text text-body-2">
                  {{ $t("no_printers_found") }}
                </p>
                <v-btn color="primary" outlined @click="navigateToPrinters">
                  <v-icon left small>fal fa-plus</v-icon>
                  {{ $t("add_printer") }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row v-else dense>
              <v-col
                v-for="printer in printers"
                :key="printer.id || printer.name"
                cols="12"
                sm="6"
                lg="4"
              >
                <v-sheet outlined rounded class="printer-test-tile pa-3 h-100">
                  <div class="d-flex align-start">
                    <span
                      class="printer-test-tile__dot mt-1 mr-2 flex-shrink-0"
                      :class="
                        printer.connection_status === 'online'
                          ? 'printer-test-tile__dot--on'
                          : 'printer-test-tile__dot--off'
                      "
                      aria-hidden="true"
                    />
                    <div class="printer-test-tile__body">
                      <div class="font-weight-medium text-body-1 text-truncate">
                        {{ printer.name }}
                      </div>
                      <div class="d-flex align-center flex-wrap mt-1">
                        <v-chip
                          x-small
                          outlined
                          :color="
                            printer.connection_status === 'online'
                              ? 'success'
                              : 'error'
                          "
                          class="font-weight-medium"
                        >
                          {{
                            printer.connection_status === "online"
                              ? $t("online")
                              : $t("offline")
                          }}
                        </v-chip>
                        <span
                          class="text-caption grey--text text-capitalize ml-2"
                          >{{ printer.type }}</span
                        >
                      </div>
                      <div
                        v-if="printer.ip_address"
                        class="text-caption grey--text mt-1"
                      >
                        {{ printer.ip_address }}:{{ printer.port }}
                      </div>
                    </div>
                  </div>
                </v-sheet>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Languages -->
        <v-row class="mb-6">
          <v-col cols="12">
            <div class="text-subtitle-2 font-weight-medium">
              {{ $t("select_language_for_test") }}
            </div>
            <v-divider class="mt-2 mb-3" />
            <v-chip-group
              v-model="selectedLanguages"
              mandatory
              multiple
              class="printer-test-dialog__chips"
            >
              <v-chip
                v-for="lang in languages"
                :key="lang.code"
                :value="lang.code"
                filter
                outlined
                small
              >
                <LanguageFlag :code="lang.code">{{ lang.name }}</LanguageFlag>
              </v-chip>
            </v-chip-group>
          </v-col>
        </v-row>

        <!-- Preview + options -->
        <v-row>
          <v-col cols="12" md="6">
            <div class="d-flex align-center justify-space-between flex-wrap mb-2">
              <div class="text-subtitle-2 font-weight-medium">
                {{ $t("receipt_preview") }}
              </div>
              <v-btn-toggle v-model="previewMode" mandatory dense class="mt-2 mt-sm-0">
                <v-btn small value="receipt">{{ $t("receipt") }}</v-btn>
                <v-btn small value="kitchen">{{ $t("kitchen") }}</v-btn>
              </v-btn-toggle>
            </div>
            <v-divider class="mb-3" />
            <div class="printer-test-preview d-flex justify-center">
              <div
                class="printer-test-preview__paper"
                :class="previewPaperClass"
              >
                <div
                  v-if="previewMode === 'receipt'"
                  class="printer-test-preview__inner"
                >
                  <div class="printer-test-preview__headline">
                    {{ getTranslatedText("restaurant_name") }}
                  </div>
                  <div class="printer-test-preview__subhead">
                    {{ getTranslatedText("receipt") }}
                  </div>
                  <div class="printer-test-preview__block">
                    <div>{{ getTranslatedText("table") }}: T01</div>
                    <div>{{ getTranslatedText("receipt") }}: #001</div>
                    <div>
                      {{ getTranslatedText("date") }}:
                      {{ new Date().toLocaleDateString() }}
                    </div>
                    <div>
                      {{ getTranslatedText("staff") }}:
                      {{ getTranslatedText("staff_name") }}
                    </div>
                  </div>
                  <div class="printer-test-preview__dash" />
                  <div class="printer-test-preview__block">
                    <div>
                      1x {{ getTranslatedText("item_burger") }} - $10.00
                    </div>
                    <div class="printer-test-preview__note">
                      {{ getTranslatedText("no_pickles") }}
                    </div>
                    <div>2x {{ getTranslatedText("item_fries") }} - $8.00</div>
                    <div>1x {{ getTranslatedText("item_coke") }} - $2.00</div>
                  </div>
                  <div class="printer-test-preview__dash" />
                  <div class="printer-test-preview__strong">
                    {{ getTranslatedText("total") }}: $20.00
                  </div>
                  <div>
                    {{ getTranslatedText("payment") }}:
                    {{ getTranslatedText("cash") }}
                  </div>
                  <div>{{ getTranslatedText("change") }}: $5.00</div>
                  <div class="printer-test-preview__footer">
                    {{ getTranslatedText("thank_you") }}
                  </div>
                </div>
                <div v-else class="printer-test-preview__inner">
                  <div class="printer-test-preview__headline">
                    {{ getTranslatedText("restaurant_name") }}
                  </div>
                  <div class="printer-test-preview__subhead">
                    {{ getTranslatedText("kitchen_order") }}
                  </div>
                  <div class="printer-test-preview__block">
                    <div>{{ getTranslatedText("table") }}: T01</div>
                    <div>{{ getTranslatedText("order") }}: #001</div>
                    <div>
                      {{ getTranslatedText("time") }}:
                      {{ new Date().toLocaleTimeString() }}
                    </div>
                    <div>
                      {{ getTranslatedText("staff") }}:
                      {{ getTranslatedText("staff_name") }}
                    </div>
                  </div>
                  <div class="printer-test-preview__dash" />
                  <div class="printer-test-preview__block">
                    <div class="printer-test-preview__strong">
                      1x {{ getTranslatedText("item_burger") }}
                    </div>
                    <div class="printer-test-preview__note">
                      {{ getTranslatedText("no_pickles") }}
                    </div>
                    <div class="printer-test-preview__strong">
                      2x {{ getTranslatedText("item_fries") }}
                    </div>
                    <div class="printer-test-preview__strong">
                      1x {{ getTranslatedText("item_coke") }}
                    </div>
                  </div>
                  <div class="printer-test-preview__kitchen-end">
                    {{ getTranslatedText("end_of_order") }}
                  </div>
                </div>
              </div>
            </div>
          </v-col>

          <v-col cols="12" md="6">
            <div class="text-subtitle-2 font-weight-medium">
              {{ $t("test_options") }}
            </div>
            <v-divider class="mt-2 mb-3" />

            <v-select
              v-model="selectedPrinter"
              :items="availablePrinters"
              :label="$t('select_printer')"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
              :disabled="availablePrinters.length === 0"
            />

            <v-select
              v-model="paperSize"
              :items="paperSizes"
              :label="$t('paper_size')"
              item-text="name"
              item-value="value"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
            />

            <v-select
              v-model="printType"
              :items="printTypes"
              :label="$t('print_type')"
              item-text="name"
              item-value="value"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            />

            <div
              class="text-overline grey--text mb-2"
              style="letter-spacing: 0.06em;"
            >
              {{ $t("test_printing") }}
            </div>
            <v-btn
              block
              color="primary"
              depressed
              :disabled="!selectedPrinter || testing"
              :loading="testing"
              class="mb-2"
              @click="testDirectPrint"
            >
              <v-icon left small>fal fa-print</v-icon>
              {{ $t("test_direct_print") }}
            </v-btn>
            <v-btn
              block
              outlined
              color="primary"
              :disabled="testing"
              :loading="testingPDF"
              class="mb-2"
              @click="testPDFPrint"
            >
              <v-icon left small>fal fa-file-pdf</v-icon>
              {{ $t("test_pdf_print") }}
            </v-btn>
            <v-btn
              block
              outlined
              color="primary"
              :disabled="!selectedPrinter || testingEnglish"
              :loading="testingEnglish"
              class="mb-2"
              @click="testEnglishPrint"
            >
              <v-icon left small>fal fa-font</v-icon>
              {{ $t("test_english_only") }}
            </v-btn>

            <v-alert
              v-if="testResult"
              :type="testResult.success ? 'success' : 'error'"
              text
              dense
              class="mt-4"
              border="left"
            >
              <div class="font-weight-medium">{{ testResult.message }}</div>
              <div v-if="testResult.details" class="text-caption mt-2">
                <div
                  v-for="(value, key) in testResult.details"
                  :key="key"
                  class="mt-1"
                >
                  <strong>{{ key }}:</strong> {{ value }}
                </div>
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions class="px-4 pb-4 pt-0">
        <v-spacer />
        <v-btn text color="primary" @click="closeDialog">
          {{ $t("close") }}
        </v-btn>
      </v-card-actions>
    </div>
  </app-custom-dialog>
</template>

<script>
import PrinterService from "@/services/printerService";
import { success, errorAlert } from "@/controllers/alert";

export default {
  name: "PrinterTestDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      refreshing: false,
      testing: false,
      testingPDF: false,
      testingEnglish: false,

      // Test options
      selectedLanguages: ["en", "lo"], // Default to English and Lao
      previewMode: "receipt",
      selectedPrinter: null,
      paperSize: 58,
      printType: "receipt",

      // Results
      testResult: null,

      // Data
      printerService: null,
      printers: [], // Local printers array loaded from API

      // Options
      languages: [
        { code: "en", name: "English" },
        { code: "lo", name: "Lao" },
        { code: "th", name: "Thai" },
        { code: "zh", name: "Chinese" },
      ],

      paperSizes: [
        { name: "58mm", value: 58 },
        { name: "80mm", value: 80 },
        { name: "A4", value: "a4" },
      ],

      printTypes: [
        { name: "Receipt", value: "receipt" },
        { name: "Kitchen Order", value: "kitchen" },
        { name: "Test Page", value: "test" },
      ],

      // Translation data for testing
      translations: {
        en: {
          restaurant_name: "MY RESTAURANT",
          receipt: "RECEIPT",
          kitchen_order: "KITCHEN ORDER",
          table: "Table",
          order: "Order",
          date: "Date",
          time: "Time",
          staff: "Staff",
          staff_name: "John Doe",
          item_burger: "Burger",
          item_fries: "French Fries",
          item_coke: "Coca Cola",
          no_pickles: "No pickles",
          total: "TOTAL",
          payment: "Payment",
          cash: "Cash",
          change: "Change",
          thank_you: "Thank you for dining!",
          end_of_order: "END OF ORDER",
        },
        lo: {
          restaurant_name: "ຮ້ານອາຫານຂອງຂ້ອຍ",
          receipt: "ໃບຍັ່ຮັບ",
          kitchen_order: "ອໍເດີຫ້ອງຄົວ",
          table: "ໂຕະໂະ",
          order: "ອໍເດີ",
          date: "ວັນທີ່",
          time: "ເວລາ",
          staff: "ພະນັກງານ",
          staff_name: "ສົມຊາຍ ໂພນ",
          item_burger: "ເບີເກີ",
          item_fries: "ໝຣັງ",
          item_coke: "ໂກກໂກ",
          no_pickles: "ບໍ່ໃສ່ພິກິ້ວ",
          total: "ລວມທັງໝົດ",
          payment: "ການຊຳລະ",
          cash: "ເງິນສົດ",
          change: "ເງິນທອນ",
          thank_you: "ຂອບໃຈ!",
          end_of_order: "ສິ້ນສຸດອໍເດີ",
        },
        th: {
          restaurant_name: "ร้านอาหารของฉัน",
          receipt: "ใบเสร็จ",
          kitchen_order: "ออเดอร์ครัว",
          table: "โต๊ะ",
          order: "ออเดอร์",
          date: "วันที่",
          time: "เวลา",
          staff: "พนักงาน",
          staff_name: "สมชาย ใจดี",
          item_burger: "เบอร์เกอร์",
          item_fries: "เฟรนช์ฟราย",
          item_coke: "โค้ก",
          no_pickles: "ไม่ใส้เดริก",
          total: "รวม",
          payment: "การชำระ",
          cash: "เงินสด",
          change: "เงินทอน",
          thank_you: "ขอบคุณค่ะ",
          end_of_order: "จบออเดอร์",
        },
        zh: {
          restaurant_name: "我的餐厅",
          receipt: "收据",
          kitchen_order: "厨房订单",
          table: "桌号",
          order: "订单",
          date: "日期",
          time: "时间",
          staff: "员工",
          staff_name: "张三",
          item_burger: "汉堡",
          item_fries: "薯条",
          item_coke: "可乐",
          no_pickles: "不要腌黄瓜",
          total: "总计",
          payment: "付款",
          cash: "现金",
          change: "找零",
          thank_you: "谢谢用餐！",
          end_of_order: "订单结束",
        },
      },
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },

    availablePrinters() {
      const printers = this.printers || [];
      if (!Array.isArray(printers)) {
        return [];
      }
      return printers.filter(
        (printer) => printer && printer.connection_status === "online"
      );
    },

    previewPaperClass() {
      if (this.paperSize === 58) {
        return "printer-test-preview__paper--58";
      }
      if (this.paperSize === 80) {
        return "printer-test-preview__paper--80";
      }
      return "printer-test-preview__paper--a4";
    },
  },

  watch: {
    show(value) {
      if (value) {
        this.initializePrinterService();
        this.refreshPrinters();
      } else {
        this.testResult = null;
      }
    },
    printers: {
      handler(newPrinters) {
        // Auto-select first online printer when printers change
        if (newPrinters && Array.isArray(newPrinters)) {
          const availablePrinters = newPrinters.filter(
            (printer) => printer.connection_status === "online"
          );
          if (availablePrinters.length > 0 && !this.selectedPrinter) {
            this.selectedPrinter = availablePrinters[0].id;
          }
        }
      },
      deep: true,
    },
    selectedLanguages: {
      handler(newValue) {
        // Ensure selectedLanguages is always an array
        // v-chip-group can return a single value instead of an array
        if (!Array.isArray(newValue)) {
          this.selectedLanguages = newValue ? [newValue] : ["en"];
        }
      },
      immediate: true,
    },
  },

  methods: {
    async initializePrinterService() {
      if (!this.printerService) {
        this.printerService = new PrinterService(this.$http);
      }
    },

    /**
     * Navigate to printers page, avoiding redundant navigation errors
     */
    navigateToPrinters() {
      // Check if we're already on the printers route
      if (this.$route.path !== "/printers") {
        this.$router.push("/printers").catch((err) => {
          // Ignore navigation duplicated errors (Vue Router 3.x behavior)
          if (
            err.name !== "NavigationDuplicated" &&
            err.name !== "NavigationDuplicatedError"
          ) {
            console.error("Navigation error:", err);
          }
        });
      } else {
        // If already on printers page, emit event to parent to show add printer dialog
        // and close this dialog
        this.$emit("show-add-printer");
        this.closeDialog();
      }
    },

    /**
     * Load printers directly from API (similar to printers.vue)
     */
    async loadPrinters() {
      try {
        const { data } = await this.$http.get("/printers");

        if (data && data.status && data.data) {
          // Transform API response to match frontend format
          this.printers = data.data.map((printer) => ({
            ...printer,
            print_categories: printer.print_category
              ? [printer.print_category]
              : [],
            connection_status: printer.connection_status || "offline",
          }));
        } else {
          this.printers = [];
        }
      } catch (error) {
        console.error("Error loading printers:", error);
        this.printers = [];
      }
    },

    async refreshPrinters() {
      this.refreshing = true;
      try {
        await this.loadPrinters();
        // Auto-select first online printer if none selected
        if (this.availablePrinters.length > 0 && !this.selectedPrinter) {
          this.selectedPrinter = this.availablePrinters[0].id;
        }
      } catch (error) {
        console.error("Failed to refresh printers:", error);
      } finally {
        this.refreshing = false;
      }
    },

    getTranslatedText(key) {
      // Ensure selectedLanguages is always an array
      const languages = Array.isArray(this.selectedLanguages)
        ? this.selectedLanguages
        : this.selectedLanguages
        ? [this.selectedLanguages]
        : ["en"]; // Default to English if empty

      // Return concatenated translations for selected languages
      return languages
        .map((lang) => this.translations[lang]?.[key] || key)
        .join(" / ");
    },

    async testDirectPrint() {
      if (!this.printerService || !this.selectedPrinter) {
        this.testResult = {
          success: false,
          message: "No printer service or printer selected",
        };
        return;
      }

      this.testing = true;
      this.testResult = null;

      try {
        const printers = this.printers || [];
        const printer = printers.find(
          (p) => p && p.id === this.selectedPrinter
        );

        if (!printer) {
          throw new Error("Printer not found");
        }

        const testContent = this.generateTestContent(printer);

        let result;
        if (this.printType === "test") {
          result = await this.printerService.printEnglishTest(printer);
        } else if (this.printType === "receipt") {
          result = await this.printerService.print(printer, {
            content: testContent,
            options: {
              type: "receipt",
              language: this.selectedLanguages.join(","),
            },
          });
        } else if (this.printType === "kitchen") {
          result = await this.printerService.print(printer, {
            content: testContent,
            options: {
              type: "kitchen",
              language: this.selectedLanguages.join(","),
            },
          });
        }

        this.testResult = result;

        if (result.success) {
          await success(this, this.$t("print_test_successful"));
        } else {
          await errorAlert(this, result.message);
        }
      } catch (error) {
        this.testResult = {
          success: false,
          message: error.message,
          details: { error: error.toString() },
        };
      } finally {
        this.testing = false;
      }
    },

    async testPDFPrint() {
      if (!this.printerService) {
        this.testResult = {
          success: false,
          message: "No printer service available",
        };
        return;
      }

      this.testingPDF = true;
      this.testResult = null;

      try {
        const printer = { id: "pdf", name: "PDF Printer" };
        const testData = this.generateReceiptData();

        const result = await this.printerService.printAsPDF(printer, testData, {
          pdfOptions: {
            filename: `test_receipt_${Date.now()}.pdf`,
            jsPDF: { format: "a4" },
          },
        });

        this.testResult = result;

        if (result.success) {
          await success(this, this.$t("pdf_test_successful"));
        } else {
          await errorAlert(this, result.message);
        }
      } catch (error) {
        this.testResult = {
          success: false,
          message: error.message,
          details: { error: error.toString() },
        };
      } finally {
        this.testingPDF = false;
      }
    },

    async testEnglishPrint() {
      if (!this.printerService || !this.selectedPrinter) {
        this.testResult = {
          success: false,
          message: "No printer service or printer selected",
        };
        return;
      }

      this.testingEnglish = true;
      this.testResult = null;

      try {
        const printers = this.printers || [];
        const printer = printers.find(
          (p) => p && p.id === this.selectedPrinter
        );

        if (!printer) {
          throw new Error("Printer not found");
        }

        const result = await this.printerService.printEnglishTest(printer);

        this.testResult = result;

        if (result.success) {
          await success(this, this.$t("english_print_test_successful"));
        } else {
          await errorAlert(this, result.message);
        }
      } catch (error) {
        this.testResult = {
          success: false,
          message: error.message,
          details: { error: error.toString() },
        };
      } finally {
        this.testingEnglish = false;
      }
    },

    generateTestContent(printer) {
      const testItems = [
        {
          name: this.getTranslatedText("item_burger"),
          quantity: 1,
          price: 10.0,
        },
        { name: this.getTranslatedText("item_fries"), quantity: 2, price: 4.0 },
        { name: this.getTranslatedText("item_coke"), quantity: 1, price: 2.0 },
      ];

      const testData = {
        restaurantName: this.getTranslatedText("restaurant_name"),
        tableName: "T01",
        receiptNumber: "#001",
        orderNumber: "#001",
        staffName: this.getTranslatedText("staff_name"),
        items: testItems,
        total: 20.0,
        paymentMethod: this.getTranslatedText("cash"),
        change: 5.0,
      };

      if (this.printType === "receipt") {
        return this.printerService.generateReceiptPrint(testData, printer);
      } else if (this.printType === "kitchen") {
        return this.printerService.generateKitchenOrderPrint(testData, printer);
      } else {
        return this.printerService.generateTestPrint(printer, "basic");
      }
    },

    generateReceiptData() {
      const testItems = [
        {
          name: this.getTranslatedText("item_burger"),
          quantity: 1,
          price: 10.0,
          modifications: this.getTranslatedText("no_pickles"),
        },
        { name: this.getTranslatedText("item_fries"), quantity: 2, price: 4.0 },
        { name: this.getTranslatedText("item_coke"), quantity: 1, price: 2.0 },
      ];

      return {
        restaurantName: this.getTranslatedText("restaurant_name"),
        tableName: "T01",
        receiptNumber: "#001",
        staffName: this.getTranslatedText("staff_name"),
        items: testItems,
        total: 20.0,
        paymentMethod: this.getTranslatedText("cash"),
        change: 5.0,
      };
    },

    closeDialog() {
      this.show = false;
    },
  },
};
</script>

<style scoped>
.printer-test-dialog__chips ::v-deep .v-slide-group__content {
  padding-top: 4px;
}

.printer-test-tile__body {
  min-width: 0;
}

.printer-test-tile__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.printer-test-tile__dot--on {
  background-color: #4caf50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.25);
}

.printer-test-tile__dot--off {
  background-color: #f44336;
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.2);
}

.printer-test-preview {
  width: 100%;
  padding: 8px 0 4px;
}

.printer-test-preview__paper {
  margin: 0 auto;
  padding: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 1.45;
  background-color: #fff;
  white-space: normal;
  word-break: break-word;
}

.theme--dark .printer-test-preview__paper {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: #1e1e1e;
}

.printer-test-preview__paper--58 {
  width: 232px;
  min-height: 320px;
}

.printer-test-preview__paper--80 {
  width: 320px;
  min-height: 360px;
}

.printer-test-preview__paper--a4 {
  width: 100%;
  max-width: 380px;
  min-height: 400px;
}

.printer-test-preview__inner {
  color: inherit;
}

.printer-test-preview__headline {
  text-align: center;
  font-weight: 700;
  margin-bottom: 8px;
}

.printer-test-preview__subhead {
  text-align: center;
  margin-bottom: 16px;
}

.printer-test-preview__block {
  margin-bottom: 16px;
}

.printer-test-preview__dash {
  border-top: 1px dashed rgba(0, 0, 0, 0.18);
  margin: 8px 0;
}

.theme--dark .printer-test-preview__dash {
  border-top-color: rgba(255, 255, 255, 0.18);
}

.printer-test-preview__note {
  margin-left: 20px;
  font-size: 0.92em;
  opacity: 0.92;
}

.printer-test-preview__strong {
  font-weight: 700;
}

.printer-test-preview__footer {
  text-align: center;
  margin-top: 20px;
  margin-bottom: 8px;
}

.printer-test-preview__kitchen-end {
  text-align: center;
  margin-top: 20px;
}
</style>
